// Read-only evidence register for an authenticated manager reviewing checks.
// Never use untrusted URLs from a document record; bytes always pass through
// CareDock's existing protected, workspace-scoped employee document endpoint.
export const CHECK_GROUPS = [
  ['right_to_work', 'Right to Work'],
  ['dbs', 'DBS'],
  ['identity', 'Identity'],
  ['proof_of_address', 'Proof of Address'],
];
const retired = new Set([
  'deleted', 'removed', 'superseded', 'rejected', 'invalidated',
  'uploaded_in_error', 'replacement_requested', 'archived', 'expired',
  'misfiled', 'moved',
]);
const hasUsableId = (value) => typeof value === 'string' &&
  value.length > 0 && value.length <= 180 && /^[a-zA-Z0-9_.:-]+$/.test(value);

export function collectVerificationWorkbenchFiles(payload, focus = 'identity') {
  const raw = payload?.sections || payload?.compliance_sections || {};
  const sections = Array.isArray(raw)
    ? Object.fromEntries(raw.filter(Boolean).map(s => [s.key || s.section_key || s.requirement_id, s]))
    : raw;
  const all = [], seen = new Set();
  const push = (doc, group, label, isProof = false) => {
    const id = String(doc?.id || doc?.file_id || doc?.document_id || '').trim();
    if (!hasUsableId(id) || seen.has(id)) return;
    if (doc.file_unavailable || doc.bytes_unavailable ||
        ['retired', 'unavailable', 'retrieval_failed'].includes(String(doc.storage_availability || '').toLowerCase())) return;
    if (retired.has(String(doc.status || '').toLowerCase()) ||
        retired.has(String(doc.review_status || '').toLowerCase())) return;
    seen.add(id);
    all.push({
      id, group, isProof,
      name: String(doc?.file_name || doc?.original_filename || doc?.filename || label || 'Evidence document').slice(0, 120),
    });
  };
  for (const [group, label] of CHECK_GROUPS) {
    const section = sections?.[group] || {};
    const rows = Array.isArray(section.rows) ? section.rows : [];
    const evidence = rows.find(row => row?.row_type === 'evidence') || {};
    // Use the full current evidence list, not the 3-document dashboard preview.
    const available = Array.isArray(evidence.active_documents)
      ? evidence.active_documents : evidence.documents_preview;
    for (const doc of Array.isArray(available) ? available : []) {
      push(doc, group, label);
    }
    const check = rows.find(row => row?.row_type === 'check')?.check_data || {};
    const proof = rows.find(row => row?.row_type === 'proof') || {};
    // A proof ID alone is NOT evidence of current worker ownership. The
    // compliance-file endpoint includes a nested proof document ONLY when it
    // found that source among this employee's tenant-scoped documents.
    const linked = check.proof_document || check.evidence_document || proof.proof_document || null;
    const linkedId = check.proof_document_id || check.evidence_document_id ||
      proof.proof_document_id || proof.document_id;
    const confirmedId = String(linked?.id || linked?.file_id || '').trim();
    if (linkedId && confirmedId === String(linkedId).trim() &&
        linked?.file_unavailable !== true && linked?.file_available !== false) {
      push(linked, group, label + ' — employer check proof', true);
    }
  }
  return all.sort((a, b) => Number(b.group === focus) - Number(a.group === focus));
}

export function safeWorkbenchFileUrl(id, apiBase) {
  if (!hasUsableId(id)) return null;
  return apiBase + '/employee-documents/' + encodeURIComponent(id) + '/file';
}
